// Company Master (Feb 24 2026)
// -----------------------------------------------------------------
// Per-tenant registry of the companies/entities the tracker is
// being run for. Independent of the platform-level tenant record and
// the single-record `settings.company_info` used for branding.

import { useState, useEffect } from "react";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import Layout from "../components/Layout";
import { Plus, PencilSimple, Trash, Buildings } from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const COUNTRIES = ["India", "United States", "United Kingdom", "Germany", "China", "Japan", "France", "Italy", "Spain", "Australia", "Canada", "Brazil", "Mexico", "Russia", "South Africa", "UAE", "Saudi Arabia", "Singapore", "Malaysia", "Vietnam", "Thailand", "South Korea", "Indonesia", "Turkey", "Poland", "Netherlands", "Belgium", "Sweden", "Norway", "Denmark", "Finland", "Switzerland", "Austria", "Portugal", "Greece", "Ireland", "Israel", "Egypt", "Nigeria", "Kenya", "Argentina", "Chile", "Colombia", "Philippines", "Pakistan", "Bangladesh", "Sri Lanka", "Nepal"];
const IN_STATES = ["Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh", "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka", "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram", "Nagaland", "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal", "Delhi", "Jammu & Kashmir", "Ladakh", "Puducherry", "Chandigarh", "Andaman & Nicobar", "Dadra & Nagar Haveli", "Daman & Diu", "Lakshadweep"];

const blankForm = () => ({
  name: "", address: "", gst_no: "", city: "", state: "", country: "India",
});

export default function CompanyMaster() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blankForm());
  const [search, setSearch] = useState("");

  const fetchRows = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/company-master`);
      setRows(r.data || []);
    } catch (e) {
      toast.error("Failed to load Company Master");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRows(); }, []);

  const openAdd = () => { setEditing(null); setForm(blankForm()); setShowDialog(true); };
  const openEdit = (r) => {
    setEditing(r);
    setForm({
      name: r.name || "",
      address: r.address || "",
      gst_no: r.gst_no || "",
      city: r.city || "",
      state: r.state || "",
      country: r.country || "India",
    });
    setShowDialog(true);
  };

  const save = async () => {
    if (!form.name.trim()) {
      toast.error("Company name is required");
      return;
    }
    try {
      if (editing) {
        await axios.put(`${API}/company-master/${editing.id}`, form);
        toast.success("Company updated");
      } else {
        await axios.post(`${API}/company-master`, form);
        toast.success("Company added");
      }
      setShowDialog(false);
      fetchRows();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete '${r.name}'? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/company-master/${r.id}`);
      toast.success("Company deleted");
      fetchRows();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const filtered = rows.filter(r => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return [r.name, r.city, r.state, r.gst_no, r.address].some(v => (v || "").toLowerCase().includes(q));
  });

  return (
    <Layout title="Company Master">
      <div className="p-6 max-w-7xl mx-auto space-y-4" data-testid="company-master-page">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-lg bg-[#1D2951] flex items-center justify-center">
              <Buildings size={22} weight="fill" className="text-[#D4A843]" />
            </div>
            <div>
              <h1 className="text-2xl font-black text-[#1D2951]">Company Master</h1>
              <p className="text-xs text-[#64748B]">Registry of the companies / branches this tracker runs for.</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Input placeholder="Search company, city, GST…"
              value={search} onChange={e => setSearch(e.target.value)}
              className="h-10 w-64" data-testid="company-search" />
            <Button onClick={openAdd} className="h-10 bg-[#1D2951] text-white gap-1" data-testid="company-add-btn">
              <Plus size={16} weight="bold" /> Add Company
            </Button>
          </div>
        </div>

        {/* Table */}
        <Card className="border border-[#E2E8F0] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="company-table">
              <thead className="bg-[#1D2951] text-white text-[11px] uppercase tracking-wider">
                <tr>
                  <th className="text-left px-3 py-2.5">Company Name</th>
                  <th className="text-left px-3 py-2.5">Address</th>
                  <th className="text-left px-3 py-2.5">GST No</th>
                  <th className="text-left px-3 py-2.5">City</th>
                  <th className="text-left px-3 py-2.5">State</th>
                  <th className="text-left px-3 py-2.5">Country</th>
                  <th className="text-right px-3 py-2.5">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={7} className="px-3 py-8 text-center text-[#94A3B8]">Loading…</td></tr>
                )}
                {!loading && filtered.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-3 py-10 text-center text-[#94A3B8]">
                      {search ? "No companies match your search." : "No companies yet — click Add Company to create your first entry."}
                    </td>
                  </tr>
                )}
                {filtered.map((r, i) => (
                  <tr key={r.id} className={i % 2 === 0 ? "bg-white" : "bg-[#FAFBFC]"} data-testid={`company-row-${r.id}`}>
                    <td className="px-3 py-2.5 font-semibold text-[#1D2951]">{r.name}</td>
                    <td className="px-3 py-2.5 text-[#334155] max-w-[280px] truncate" title={r.address}>{r.address || "—"}</td>
                    <td className="px-3 py-2.5 text-[#334155] font-mono text-xs">{r.gst_no || "—"}</td>
                    <td className="px-3 py-2.5 text-[#334155]">{r.city || "—"}</td>
                    <td className="px-3 py-2.5 text-[#334155]">{r.state || "—"}</td>
                    <td className="px-3 py-2.5 text-[#334155]">{r.country || "—"}</td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-1.5 justify-end">
                        <Button variant="ghost" size="sm" onClick={() => openEdit(r)}
                          className="h-8 text-[#1D2951] hover:bg-[#F1F5F9]"
                          data-testid={`company-edit-${r.id}`}>
                          <PencilSimple size={14} weight="bold" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => remove(r)}
                          className="h-8 text-red-600 hover:bg-red-50"
                          data-testid={`company-delete-${r.id}`}>
                          <Trash size={14} weight="bold" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        <p className="text-[11px] text-[#94A3B8]">
          {filtered.length} of {rows.length} companies
        </p>
      </div>

      {/* Add / Edit dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl" data-testid="company-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-black text-[#1D2951]">
              {editing ? "Edit Company" : "Add Company"}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2">
            <div className="md:col-span-2">
              <Label className="text-xs font-semibold text-[#334155]">Company Name *</Label>
              <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                className="mt-1" placeholder="e.g. Swami Precision Engineers"
                data-testid="company-name-input" />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs font-semibold text-[#334155]">Address</Label>
              <Input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })}
                className="mt-1" placeholder="Plot no, street, area…"
                data-testid="company-address-input" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-[#334155]">GST Number</Label>
              <Input value={form.gst_no} onChange={e => setForm({ ...form, gst_no: e.target.value.toUpperCase() })}
                className="mt-1 font-mono" placeholder="22AAAAA0000A1Z5"
                data-testid="company-gst-input" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-[#334155]">City</Label>
              <Input value={form.city} onChange={e => setForm({ ...form, city: e.target.value })}
                className="mt-1" placeholder="Pune"
                data-testid="company-city-input" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-[#334155]">State</Label>
              {form.country === "India" ? (
                <select value={form.state} onChange={e => setForm({ ...form, state: e.target.value })}
                  className="mt-1 w-full h-10 border border-[#CBD5E1] rounded px-2 text-sm bg-white"
                  data-testid="company-state-input">
                  <option value="">— Select state —</option>
                  {IN_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              ) : (
                <Input value={form.state} onChange={e => setForm({ ...form, state: e.target.value })}
                  className="mt-1" placeholder="State / Province"
                  data-testid="company-state-input" />
              )}
            </div>
            <div>
              <Label className="text-xs font-semibold text-[#334155]">Country</Label>
              <select value={form.country} onChange={e => setForm({ ...form, country: e.target.value })}
                className="mt-1 w-full h-10 border border-[#CBD5E1] rounded px-2 text-sm bg-white"
                data-testid="company-country-input">
                {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => setShowDialog(false)} data-testid="company-cancel-btn">
              Cancel
            </Button>
            <Button onClick={save} className="bg-[#1D2951] text-white" data-testid="company-save-btn">
              {editing ? "Save Changes" : "Add Company"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
